import React, { useCallback, useState } from 'react';

// Tiny 📋 → ✓ button that copies its `text` prop to the clipboard.
// Hidden by default; reveals on parent group-hover (the parent node
// MUST carry the `group` Tailwind class). Clicks stopPropagation so
// the parent node's own onClick (focusBubble / focusToolCall) doesn't
// also fire when the operator just wants to grab the text.
//
// 🟢 OPERATOR GOLD per the delight-craftsman triad — debugging from
// the ChatMap routinely needs the exact prompt / response / args
// text in a different surface (another model, a doc, a paste-back
// snapshot). One click instead of "expand pill → select → copy."
export interface CopyChipProps {
  text: string;
  /** Accessible label / hover tooltip ("Copy answer", "Copy args", etc). */
  label?: string;
  /** Optional override for absolute positioning. Default: top-1 right-1. */
  className?: string;
}

export const CopyChip: React.FC<CopyChipProps> = ({
  text,
  label = 'Copy',
  className = 'top-1 right-1',
}) => {
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!text || !navigator.clipboard) return;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 800);
        })
        .catch(() => {
          // Clipboard write can fail on insecure contexts / older
          // browsers; silent failure is fine — the operator will
          // notice the lack of ✓ feedback and try again or fall back.
        });
    },
    [text],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      title={copied ? 'Copied!' : label}
      aria-label={label}
      className={`absolute ${className} z-10 opacity-0 group-hover:opacity-100 transition-opacity
        text-[10px] leading-none px-1.5 py-1 rounded
        bg-gray-900/70 hover:bg-gray-900/90
        text-gray-200 hover:text-white
        border border-white/10
        ${copied ? '!opacity-100 text-emerald-300 border-emerald-500/40' : ''}`}
    >
      {copied ? '✓' : '📋'}
    </button>
  );
};

export default CopyChip;
